/**
 * Registration Component — Direction C reskin
 *
 * Two-column layout: left panel with trust signals + emotional support,
 * right panel with the WorkOS registration CTA.
 *
 * WorkOS handles all authentication methods (email/password, Google OAuth, etc.)
 * Identity verification happens post-registration via /verify route.
 */
import React, { useEffect, useState } from 'react';
import { useAuth } from '@shared/context';
import { Navigate, useSearchParams } from 'react-router-dom';
import Loader from '../loader/loader.component';
import { stashRegistrationConsent } from '../../shared/registration-consent';
import { getDefaultRouteForRoles } from '../../shared/role-default-route';

import './register.scss';

const Register = () => {
  const { signUp, isAuthenticated, isLoading, isAuthConfigured, user } = useAuth();
  const [searchParams] = useSearchParams();
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);

  // Capture partner slug from URL (?partner=hgfg) into sessionStorage
  // so it survives the WorkOS auth redirect
  useEffect(() => {
    const partner = searchParams.get('partner');
    if (partner) {
      sessionStorage.setItem('esig-partner-slug', partner);
    }
  }, [searchParams]);

  // If already authenticated, redirect to the role-appropriate dashboard.
  // A staff user who somehow lands on /register (stale tab, marketing-site
  // link, etc.) must NOT be tossed at /accounts/me — see bug 1AC21B.
  if (isAuthenticated) {
    return <Navigate to={getDefaultRouteForRoles(user?.roles || [])} replace />;
  }

  const onCreateAccount = () => {
    if (!termsAgreed) return;
    // Capture consent values + the click timestamp now, before WorkOS
    // sends us off the page. The legally-binding moment is this click,
    // not whenever the post-auth mutation eventually lands.
    stashRegistrationConsent({
      tosAcceptedAt: new Date().toISOString(),
      smsConsent,
    });
    signUp();
  };

  return (
    <section className="join-page">
      <div className="join-layout">
        {/* Left: trust + reassurance panel */}
        <div className="join-trust">
          <div className="join-trust__inner">
            <a href="https://esignatureguarantee.com" className="join-trust__logo">
              <img src="/assets/images/logos/esig-icon.svg" alt="" width="28" height="28" />
              <span className="join-trust__wordmark">eSignature Guarantee</span>
            </a>

            <h1 className="join-trust__headline">
              Get the stamp your bank <em>wouldn't give you.</em>
            </h1>

            <p className="join-trust__dek">
              You've been sent here by a transfer agent. Creating an account takes
              30 seconds — we'll guide you through everything after that.
            </p>

            <div className="join-trust__stats">
              <div className="join-trust__stat">
                <span className="join-trust__stat-number">25,000+</span>
                <span className="join-trust__stat-label">stamps issued</span>
              </div>
              <div className="join-trust__stat">
                <span className="join-trust__stat-number">All 50</span>
                <span className="join-trust__stat-label">states served</span>
              </div>
              <div className="join-trust__stat">
                <span className="join-trust__stat-number">STAMP</span>
                <span className="join-trust__stat-label">member · 17Ad-15</span>
              </div>
            </div>

            <blockquote className="join-trust__quote">
              <p>
                "My mom passed and the last thing I wanted was more paperwork.
                They told me exactly what to send and handled the rest."
              </p>
              <cite>— Emily P., California · Estate transfer</cite>
            </blockquote>

            <div className="join-trust__creds">
              <span>STAMP Program Member</span>
              <span className="join-trust__creds-dot">·</span>
              <span>Bonded & Insured</span>
              <span className="join-trust__creds-dot">·</span>
              <span>Accepted by all major transfer agents</span>
            </div>
          </div>
        </div>

        {/* Right: registration form */}
        <div className="join-form">
          <div className="join-form__inner">
            <div className="join-form__card">
              <h2 className="join-form__title">Create an account</h2>
              <p className="join-form__subtitle">
                Sign up with your email or Google account.
                It takes less than a minute.
              </p>

              {/* Legal links live outside the <label> — clicks on a nested
                  <a> don't reliably toggle the checkbox (browser opens the
                  link instead). Pulling the links out keeps the row's click
                  area click-to-toggle and lets the links be independent. */}
              <div className="join-form__consent">
                <div className="join-form__consent-row">
                  <input
                    id="join-terms-agree"
                    type="checkbox"
                    checked={termsAgreed}
                    onChange={(e) => setTermsAgreed(e.target.checked)}
                  />
                  <label htmlFor="join-terms-agree">
                    I agree to the{' '}
                    <a
                      href="https://esignatureguarantee.com/terms"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Terms of Service
                    </a>{' '}
                    and{' '}
                    <a
                      href="https://esignatureguarantee.com/privacy"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Privacy Policy
                    </a>
                    .
                  </label>
                </div>
                <div className="join-form__consent-row">
                  <input
                    id="join-sms-consent"
                    type="checkbox"
                    checked={smsConsent}
                    onChange={(e) => setSmsConsent(e.target.checked)}
                  />
                  <label htmlFor="join-sms-consent">
                    <em>Optional:</em> I consent to receive SMS notifications about
                    my transactions.
                  </label>
                </div>
              </div>

              {!isAuthConfigured && (
                <div
                  className="join-form__error"
                  role="alert"
                  style={{
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    color: '#991b1b',
                    borderRadius: 6,
                    padding: '12px 14px',
                    marginBottom: 16,
                    fontSize: 14,
                  }}
                >
                  Account creation is temporarily unavailable. Please refresh
                  the page or contact support at{' '}
                  <a href="mailto:support@esignatureguarantee.com">
                    support@esignatureguarantee.com
                  </a>
                  .
                </div>
              )}

              <div className="join-form__action">
                {isLoading ? (
                  <Loader />
                ) : (
                  <button
                    name="create-account"
                    type="button"
                    className="join-form__btn"
                    onClick={onCreateAccount}
                    disabled={!termsAgreed || !isAuthConfigured}
                    aria-disabled={!termsAgreed || !isAuthConfigured}
                  >
                    Create account
                  </button>
                )}
              </div>

              <div className="join-form__info">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 16v-4"/>
                  <path d="M12 8h.01"/>
                </svg>
                <span>After registration, you'll verify your identity to complete transactions.</span>
              </div>
            </div>

            <p className="join-form__login">
              Already have an account? <a href="/login">Log in</a>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Register;
